import moment, { Moment } from 'moment-timezone';

interface WeekRange {
  from: Moment;
  to: Moment;
}

export type ISOWeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Calculates the current week’s start and end dates based on a custom “first day”.
 *
 * @param startOfWeek - Which weekday the week begins on (0=Sunday, 1=Monday, …, 6=Saturday).
 * @returns An object containing:
 *   - `from`: Moment for the first day of that week
 *   - `to`:   Moment for the last day of that week
 *
 * @example
 * // If today is Tuesday, 29 April 2025:
 * const { from, to } = getStartAndEndOfWeek(2); // 2 = Tuesday
 * console.log(from.format('YYYY-MM-DD')); // '2025-04-29' (Tuesday)
 * console.log(to.format('YYYY-MM-DD'));   // '2025-05-05' (Monday)
 */
export const getStartAndEndOfWeek = (startOfWeek: ISOWeekDay = 0): WeekRange => {
  const today: Moment = moment();

  // Get weekday of today (0=Sunday…6=Saturday)
  const currentDay: number = today.day();

  // How many days to step back to hit `startOfWeek`
  // (add 7 before mod to avoid negatives)
  const daysBack: number = (currentDay - startOfWeek + 7) % 7;

  // Compute the start-of-week by subtracting daysBack
  const weekStart: Moment = today.clone().subtract(daysBack, 'days');

  // End-of-week is 6 days after the start
  const weekEnd: Moment = weekStart.clone().add(6, 'days');

  return { from: weekStart, to: weekEnd };
};
